import React, { useContext, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Login from "./components/auth/Login";
import Register from "./components/auth/Register";
import AuthContext from "./context/AuthContext";
import FrontPage from "./components/mainlayout/body/FrontPage";
import HomePage from "./components/mainlayout/body/HomePage";
import ScenarioTitlePage from "./components/scenarios/create/ScenarioTitlePage";
import MainLayout from "./components/mainlayout/MainLayout";
import Search from "./components/search/Search";
import LoggingOut from "./components/auth/LoggingOut";
import VerifyEmail from "./components/auth/VerifyEmail";
import ForgotPassword from "./components/auth/ForgotPassword";
import ResetPassword from "./components/auth/ResetPassword";
import Browse from "./components/search/Browse";
import About from "./components/about/About";
import MyClassrooms from "./components/classrooms/MyClassrooms";
import TableOfContents from "./components/scenarios/create/TableOfContents";
import CreateScenario from "./components/scenarios/create/CreateScenario";
import ViewScenario from "./components/scenarios/view/ViewScenario";
import Account from "./components/profile/Account";
import AuthForm from "./components/auth/AuthForm";
import AcceptInvite from "./components/classrooms/AcceptInvite";
import { ClassroomContextProvider } from "./context/ClassroomContext";
import { ReactFlowProvider } from "reactflow";
import { CanvasProvider } from "./context/CanvasContext";
import { ReadOnlyCanvasProvider } from "./hooks/useReadOnlyCanvas";
import HeatmapViewer from "./components/heatmap/HeatmapViewer";
import ClassroomCanvasViewer from "./components/classrooms/classroomcanvas/ClassroomCanvasViewer";
import StripeCheckout from "./components/stripe/StripeCheckout";
import CancelSubscriptionPage from "./components/stripe/CancelSubscriptionPage";
import SuccessPage from "./components/stripe/SuccessPage";
import AccountSettings from "./components/profile/AccountSettings";
import GlobalStylesTesting from "./components/playground/GlobalStylesTesting";
import GraphBackground from "./components/mainlayout/body/GraphBackground";
import SendSiteFeedback from "./components/feedback/SendSiteFeedback";
import SendSiteFeedbackAdmin from "./components/feedback/SendSiteFeedbackAdmin";
import AdminPage from "./components/adminpage/AdminPage";
import ClassroomGrades from "./components/classrooms/ClassroomGrades";
import AdminCanvas from "./components/adminpage/admincanvas/AdminCanvas";
import AdminCanvasViewer from "./components/adminpage/admincanvas/AdminCanvasViewer";
import StripeOuter from "./components/stripe/StripeOuter";
import OnboardingComplete from "./components/stripe/StripeOnboarding";
import ScenarioMarket from "./components/stripe/ScenarioMarket";
import ScenarioCheckoutWrapper from "./components/stripe/ScenarioCheckoutWrapper";
import StripeLandingPage from "./components/stripe/StripeLandingPage";
import { CartProvider } from './context/CartContext';
import ScenarioCheckoutPage from "./components/stripe/ScenarioCheckoutPage";
import { PermissionsContextProvider } from "./context/PermissionsContext";
import ScenarioAbout from "./components/scenarios/view/ScenarioAbout";
import AdminGuard from "./components/guards/AdminGuard";
import SavedScenarioPage  from "./components/mainlayout/body/SavedScenarioPage";
import FollowingPage from "./components/profile/FollowingPage";
import { TutorialProvider } from "./tutorials";


import EducationScenarios from "./components/mainlayout/body/EducationScenarios";
import EducationViewAll from "./components/mainlayout/body/EducationViewAll";

// Word-import tuning page; development builds only (the import is removed from production bundles).
const DocxLab = process.env.NODE_ENV !== "production"
    ? React.lazy(() => import("./components/dev/DocxLab"))
    : null;

function Router() {
    const { loggedIn } = useContext(AuthContext);

    return (
        <BrowserRouter>
        <PermissionsContextProvider>
            {/*
              * Inside the router because a tutorial watches and drives the
              * location, and above the routes because one outlives any page.
              */}
            <TutorialProvider>
            <MainLayout>
                <Routes>
                    <Route path="*" element={null} />
                    {
                        loggedIn === false && (
                            <>
                                <Route path="/authenticate" element={<AuthForm />} />
                                <Route exact path="/" element={<FrontPage />} />
                                <Route path="/verify-email" element={<VerifyEmail />} />
                                <Route path="/forgot-password" element={<ForgotPassword />} />
                                <Route path="/reset-password" element={<ResetPassword />} />
                                <Route path="/logging-out" element={!loggedIn ? <Navigate to="/" /> : <LoggingOut />} />
                                <Route path="/home" element={!loggedIn ? <Navigate to="/" /> : <HomePage />} />
                                <Route path="/about/:scenarioId" element={<ScenarioAbout />} />
                                <Route path="/view/:scenarioId" element={<ViewScenario />} />
                                <Route path="/user/:username" element={<Account />} />
                                <Route path="/search" element={<Search />} />
                                <Route path="/browse/:tag" element={<Browse />} />
                                <Route path="/about" element={<About />} />
                                <Route path="/create/:scenarioId/canvas" element={<CreateScenario />} />
                                <Route path="/my-classrooms/accept-invite" element={<AcceptInvite />} />
                            </>
                        )
                    }
                    {
                        loggedIn === true && (
                            <>
                                <Route path="/background" element={<GraphBackground />} />
                                <Route path="/authenticate" element={<Navigate to="/home" />} />
                                <Route path="/" element={loggedIn ? <Navigate to="/home" /> : <FrontPage />} />
                                <Route path="/logging-out" element={loggedIn ? <Navigate to="/" /> : <LoggingOut />} />
                                <Route path="/verify-email" element={<VerifyEmail />} />
                                <Route exact path="/home" element={<HomePage />} />
                                <Route path="/create/:scenarioId" element={<ScenarioTitlePage />} />
                                <Route path="/create/:scenarioId/table-of-contents" element={<TableOfContents />} />
                                <Route path="/create/:scenarioId/preview" element={<ViewScenario />} />
                                <Route path="/view/:scenarioId" element={<ViewScenario />} />
                                <Route path="/about/:scenarioId" element={<ScenarioAbout />} />
                                <Route path="/view/:assignmentId/:scenarioId" element={<ViewScenario />} />
                                <Route path="/user/:username" element={<Account />} />
                                <Route path="/search" element={<Search />} />
                                <Route path="/browse/:tag" element={<Browse />} />
                                <Route path="/about" element={<About />} />
                                <Route path="/success" element={<SuccessPage />} />
                                {/* <Route path="/cancel-subscription" element={<CancelSubscriptionPage />} /> */}
                                <Route path="/account-settings" element={<AccountSettings />} />
                                <Route path="/playground" element={<GlobalStylesTesting />} />
                                {DocxLab && (
                                    <Route
                                        path="/playground/docx-lab"
                                        element={<Suspense fallback={null}><DocxLab /></Suspense>}
                                    />
                                )}
                                <Route path="/site-feedback" element={<SendSiteFeedback isAdmin={true}/>} />
                                <Route path="/site-feedback-page" element={<SendSiteFeedback isAdmin={false}/>} />
                                <Route path="/saved" element={<SavedScenarioPage />} />

                                <Route path="/following/:profileCreatorId" element={<FollowingPage />} />   
                                <Route path="/followers/:profileCreatorId" element={<FollowingPage />} />

                                <Route path="/education" element={<EducationScenarios/>} />
                                <Route path="/education/view-all" element={<EducationViewAll />} />

                                <Route element={<AdminGuard />}>
                                    <Route path="/admin" element={<AdminPage />} />
                                </Route>
                                {/* <Route path="/admin" element={<AdminPage />} /> */}


                                {/* <Route path='/onboarding/refresh' element={<OnboardingComplete />}></Route>
                                <Route path='/onboarding/complete' element={<SuccessPage />}></Route>
                                <Route path='/market' element={<ScenarioMarket />}></Route>
                                <Route path='/checkout' element={
                                    <CartProvider>
                                        <ScenarioCheckoutPage />
                                    </CartProvider>
                                    }></Route>
                                <Route path='/purchasing' element={
                                    <CartProvider>
                                        <StripeLandingPage />
                                    </CartProvider>
                                    }></Route> */}
                                
                                <Route path="/my-classrooms/*" element={
                                    <ClassroomContextProvider userId={loggedIn}>
                                        <MyClassrooms />
                                    </ClassroomContextProvider>
                                } />

                                <Route
                                    path="/logs/heatmap/:scenarioId"
                                    element={
                                        <ReactFlowProvider>
                                            <ReadOnlyCanvasProvider withHeatmap>
                                                <HeatmapViewer />
                                            </ReadOnlyCanvasProvider>
                                        </ReactFlowProvider>
                                    }
                                />

                                <Route
                                    path="/my-classrooms/:classroomId/l/:scenarioId"
                                    element={
                                        <ClassroomContextProvider userId={loggedIn}>
                                            <ReactFlowProvider>
                                                <ReadOnlyCanvasProvider>
                                                    <ClassroomCanvasViewer />
                                                </ReadOnlyCanvasProvider>
                                            </ReactFlowProvider>
                                        </ClassroomContextProvider>
                                    }
                                />
                                <Route
                                    path="/my-classrooms/:classroomId/l/:scenarioId/:assignmentId"
                                    element={
                                        <ClassroomContextProvider userId={loggedIn}>
                                            <ReactFlowProvider>
                                                <ReadOnlyCanvasProvider>
                                                    <ClassroomCanvasViewer />
                                                </ReadOnlyCanvasProvider>
                                            </ReactFlowProvider>
                                        </ClassroomContextProvider>
                                    }
                                />

                                {/* <Route path="/create/:scenarioId/canvas" element={<CreateScenario />} /> */}
                                <Route
                                    path="/create/:scenarioId/canvas"
                                    element={
                                        <ReactFlowProvider>
                                            <CanvasProvider>
                                                <CreateScenario />
                                            </CanvasProvider>
                                        </ReactFlowProvider>
                                    }
                                    />
                                


                                <Route
                                    path="/admin-view/:scenarioId"
                                    element={
                                        <ReactFlowProvider>
                                            <ReadOnlyCanvasProvider>
                                                <AdminCanvasViewer />
                                            </ReadOnlyCanvasProvider>
                                        </ReactFlowProvider>
                                    }
                                    />
                                
                                <Route path="/my-classrooms/accept-invite" element={<AcceptInvite />} />
                            </>
                        )
                    }
                </Routes>
            </MainLayout>
            </TutorialProvider>
            </PermissionsContextProvider>
        </BrowserRouter>
    );
};

export default Router;
